import {
  ContasRelatorioInadimplentes,
  RelatorioInadimplentesFields,
} from "features/relatorios/typings";
import { formatMoney } from "infra/helpers/number";
import { useMemo } from "react";
import { PDFTableColumn } from "components/PDF/Table";
import { View, Text } from "@react-pdf/renderer";
import moment from "moment";

function useRelatorioInadimplentesPdfColumns({
  selectedFields,
}: {
  selectedFields?: RelatorioInadimplentesFields[];
}): PDFTableColumn<ContasRelatorioInadimplentes>[] {
  return useMemo(() => {
    const columns: PDFTableColumn<ContasRelatorioInadimplentes>[] = [];

    if (selectedFields?.includes("composicoes")) {
      columns.push({
        title: "Composições",
        flexGrow: 2.5,
        render: (original) => (
          <View>
            {original.composicoes?.map((composicao) => (
              <Text key={composicao.id}>
                {composicao.breadcrumb} {composicao.nome}
              </Text>
            ))}
          </View>
        ),
      });
    } else {
      columns.push({
        title: "",
        flexGrow: 2.5,
        render: (original) => <View></View>,
      });
    }

    if (selectedFields?.includes("descricao")) {
      columns.push({
        title: "Descrição",
        flexGrow: 2.5,
        render: (original) => (
          <View>
            <Text>{original.descricao ?? "--"}</Text>
          </View>
        ),
      });
    } else {
      columns.push({
        title: "",
        flexGrow: 2.5,
        render: (original) => <View></View>,
      });
    }

    if (selectedFields?.includes("dataVencimento")) {
      columns.push({
        title: "Vencimento",
        flexGrow: 1.2,
        render: (original) => (
          <View>
            <Text>{moment(original.dataVencimento).format("DD/MM/YYYY")}</Text>
          </View>
        ),
      });
    } else {
      columns.push({
        title: "",
        flexGrow: 1.2,
        render: (original) => <View></View>,
      });
    }

    if (selectedFields?.includes("dataCompetencia")) {
      columns.push({
        title: "Competência",
        flexGrow: 1.3,
        render: (original) => (
          <View>
            <Text>
              {original.dataCompetencia
                ? moment(original.dataCompetencia).format("MM/YYYY")
                : "--"}
            </Text>
          </View>
        ),
      });
    } else {
      columns.push({
        title: "",
        flexGrow: 1.3,
        render: (original) => <View></View>,
      });
    }

    if (selectedFields?.includes("nossoNumero")) {
      columns.push({
        title: "Nosso Número",
        flexGrow: 1,
        textAlign: "left",
        render: (original) => (
          <View>
            <Text>{original.nossoNumero ?? "--"}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("principal")) {
      columns.push({
        title: "Principal",
        flexGrow: 1.2,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{formatMoney(original.principal)}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("multa")) {
      columns.push({
        title: "Multa",
        flexGrow: 1.2,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{formatMoney(original.multa)}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("juros")) {
      columns.push({
        title: "Juros",
        flexGrow: 1.2,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{formatMoney(original.juros)}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("valorTaxaCobranca")) {
      columns.push({
        title: `Tx. Cobrança`,
        flexGrow: 1.2,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{formatMoney(original.valorTaxaCobranca)}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("valorCorrecaoMonetaria")) {
      columns.push({
        title: "Correção \n monetária",
        flexGrow: 1.2,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{formatMoney(original.valorCorrecaoMonetaria)}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("diasAtraso")) {
      columns.push({
        title: "Dias \n atraso",
        flexGrow: 0.8,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{original.diasAtraso}</Text>
          </View>
        ),
      });
    }

    if (selectedFields?.includes("total")) {
      columns.push({
        title: "Total",
        flexGrow: 1.1,
        textAlign: "right",
        render: (original) => (
          <View>
            <Text>{formatMoney(original.total)}</Text>
          </View>
        ),
      });
    }

    return columns;
  }, [selectedFields]);
}

export default useRelatorioInadimplentesPdfColumns;
